import { memo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Button } from '@aimsos/ui';

import EntityListSkeleton from '../EntityListSkeleton.jsx';
import { AccessDeniedState, EmptyState, ErrorState } from '../states/index.js';
import { TRAITS_LIST_STATE } from '../../utils/traitsListState.js';
import TraitCard from './TraitCard.jsx';
import TraitsUnverifiedGroup from './TraitsUnverifiedGroup.jsx';

import { EyeOff, Lightbulb, SearchX } from 'lucide-react';

/**
 * Whatever the Traits list is showing (CT-5715): the rows, the skeleton, or one
 * of the four states that are not rows.
 *
 * Which of them is on screen is decided by `traitsListState` and arrives as one
 * value, so this component only paints — it re-derives nothing. The page keeps
 * the toolbar, the chips and the pager.
 *
 * @param {object} props
 * @param {string} props.state — one of `TRAITS_LIST_STATE`.
 * @param {number} props.perPage — rows the skeleton draws.
 * @param {string|null} [props.error]
 * @param {() => void} props.onRetry
 * @param {Array<object>} props.items
 * @param {{ count: number, items: Array<object> }} props.unverified
 * @param {string|null} [props.openTraitId] — the trait open in the slide-out.
 * @param {(trait: object) => void} props.onOpenTrait
 * @param {string} props.name — the record's title, for the empty copy.
 * @param {(() => void)|null} [props.onAddNote] — null where there is no
 *   Activity tab to send the user to; the action is left out rather than
 *   rendered inert.
 * @param {(() => void)|null} [props.onLogCall]
 * @param {() => void} props.onClearFilters
 */
const TraitsResults = memo(function TraitsResults({
  state,
  perPage,
  error = null,
  onRetry,
  items,
  unverified,
  openTraitId = null,
  onOpenTrait,
  name,
  onAddNote = null,
  onLogCall = null,
  onClearFilters,
}) {
  const { t } = useTranslation('unified-profiles');

  if (state === TRAITS_LIST_STATE.LOADING) return <EntityListSkeleton perPage={perPage} />;
  if (state === TRAITS_LIST_STATE.ACCESS_DENIED) return <AccessDeniedState />;
  if (state === TRAITS_LIST_STATE.ERROR) return <ErrorState message={error} onRetry={onRetry} />;

  if (state === TRAITS_LIST_STATE.RESTRICTED) {
    return (
      <EmptyState
        testId="traits-restricted"
        icon={EyeOff}
        title={t('intelligence.traits.restricted.title')}
        subtitle={t('intelligence.traits.restricted.subtitle')}
      />
    );
  }

  if (state === TRAITS_LIST_STATE.NONE_YET) {
    return (
      <EmptyState
        testId="traits-empty"
        icon={Lightbulb}
        title={t('intelligence.traits.empty.title', { name })}
        subtitle={t('intelligence.traits.empty.subtitle', { name })}
        action={
          onAddNote && onLogCall ? (
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={onAddNote}>
                {t('intelligence.traits.empty.addNote')}
              </Button>
              <Button variant="secondary" size="sm" onClick={onLogCall}>
                {t('intelligence.traits.empty.logCall')}
              </Button>
            </div>
          ) : null
        }
      />
    );
  }

  if (state === TRAITS_LIST_STATE.NO_RESULTS) {
    return (
      <EmptyState
        testId="no-results-state"
        icon={SearchX}
        title={t('intelligence.traits.noResults.title')}
        subtitle={t('intelligence.traits.noResults.subtitle')}
        action={
          <Button variant="secondary" size="sm" onClick={onClearFilters}>
            {t('intelligence.traits.noResults.clear')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {items.map((trait) => (
        <TraitCard key={trait.id} trait={trait} selected={trait.id === openTraitId} onOpen={onOpenTrait} />
      ))}
      <TraitsUnverifiedGroup unverified={unverified} openTraitId={openTraitId} onOpen={onOpenTrait} />
    </div>
  );
});

export default TraitsResults;
